import { css, html, LitElement, TemplateResult, nothing, PropertyValueMap } from "lit";
import { property, customElement } from 'lit/decorators.js';
import { FiSubView, UiStateModel, UiStateModelParam } from "../../../src/models/ui-state-model";
import { VoyageShoreContextModel } from "../../../src/models/voyage-shore-context-model";
import { FormatDateToLocalTime, GetUTCTimestamp, GetFormattedDateDayMonthTimeNoYear } from "../../../src/utils/helpers";
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import { FI_EVENT_CATEGORIES, TraceService, FI_VESSEL_LIST_ACTIONS } from "../../../src/services/trace-service";
import { VoyageSchedulesModel } from "../../../src/models/voyage-schedules-model";
import { AlarmState, AlarmType, StaticInfoForAlarms } from "../../../src/services/types/fleet-alarms-service.types";
import { VesselInfo } from "../../../src/models/types/vessels-model.types";
import { FleetAlarm } from "../../../src/models/types/voy-fleet-alarm-model.types";
import { VoyFleetAlarmModel } from "../../../src/models/voy-fleet-alarm-model";
import { TerminalsModel } from "../../../src/models/terminals-model";
import { ShoreContextEvent } from "../../../src/models/types/voyage-shore-context-model.types";
import { OperatorId, VesselVoyageVersion } from "../../voy-global-filters/src/types";
import { VoyGlobalFilters } from "../../voy-global-filters/src/voy-global-filters";
import { VoyGlobalSearch } from "../../voy-global-search/src/voy-global-search";
import { showHandleAlarm } from "../../../src/utils/alarm-utils";
import "../../voy-vessel-progress-route/src/voy-vessel-progress-route";
import "@maersk-global/mds-components-core/mc-button";
import { PremiumVesselNotInNextPlatform } from "./types";
import { getStaticInfoWithTimeRemaining } from "./helper";

@customElement('voy-fleet-sub-view-list')
export class VoyFleetSubViewList extends LitElement {
    public static emptySearchMessage = `Oops, looks like there’s no match for your search.\nPlease check your spelling or try again...`;
    public static loadingFleetMessage = `Loading fleet...`;

    @property({ type: Array }) public alarms: FleetAlarm[] = [];
    @property({ attribute: false }) public timeLineVisible: boolean = false
    @property({ type: Number }) private _totalVesselsFilteredCount: number = 0;
    @property({ type: Array }) private _vesselsShownInList: any[] = [];
    //This properties added to handle `dismiss` option for selected alarm 
    @property({ type: Array }) private _selectedAlarm: FleetAlarm | null = null;
    @property({ attribute: false }) private _handleAlarmPopupShow: boolean = false;
    @property({ attribute: false }) private _lazyLoadVesselList: any[] = [];
    @property({ attribute: false }) private _startVal: number = 200;

    constructor() {
        super();

        this._setVessels = this._setVessels.bind(this);
        this._showVesselDetails = this._showVesselDetails.bind(this);
        this._renderVessel = this._renderVessel.bind(this);
        this._renderVessels = this._renderVessels.bind(this);
        this._onAlarmsClick = this._onAlarmsClick.bind(this);
        this._onEnter = this._onEnter.bind(this);
        this._toggleAlarmPopup = this._toggleAlarmPopup.bind(this);
        this._rerenderComponent = this._rerenderComponent.bind(this);
        this._toggleTimeLine = this._toggleTimeLine.bind(this)
        this._handleScroll = this._handleScroll.bind(this);
        if(localStorage.getItem("timeLineView")!==undefined) {
            this.timeLineVisible = localStorage.getItem("timeLineView")=="true";
        }
    }

    static get styles() {
        return [
            css`
                :host {
                    display: block;
                    font-family: var(--component-paragraph-ff);
                    max-height: calc(100vh - 108px);
                    overflow: hidden auto;
                }

                section {
                    box-shadow: var(--voyage-box-shadow) 6px 0px 10px;
                }

                header {
                    padding: 24px 16px 8px 16px;
                    background-color: var(--neutrals-grey-10, white);
                }

                h1 {
                    font-family: MaerskHeadline;
                    font-size: 26px;
                    line-height: 32px;
                    margin: 0;
                    font-weight: 400;
                    padding-bottom: 24px;
                    color: var(--neutrals-grey-02);
                }

                h2.no-found {
                    padding: 16px;
                    font-size: 16px;
                    line-height: 24px;
                    letter-spacing: 0em;
                    text-align: center;
                    color: var(--neutrals-grey-03);
                }

                .vessels-container .vessel {
                    height: 76px;
                    cursor: pointer;
                    border-bottom: 1px solid var(--neutrals-grey-06)
                }

                .vessel-header,
                .voyage-status {
                    padding: 8px 16px 0;
                    display: flex;
                    align-items: center;
                    text-wrap: nowrap;
                }

                h4 {
                    margin: 0;
                    font-size: 14px;
                    flex: 1 1 auto;
                    color: var(--voyage-blue-dark);
                    height: 26px; 
                    line-height: 24px;
                }

                .vessel-header .vessel-alarm-indicators {
                    display: flex;
                    flex-direction: row;
                    column-gap: 8px;
                    justify-content: end;
                    align-items: center;
                }

                .vessel-alarm-indicators span {
                    border-radius: 30px;
                    padding-left: 1px;
                    height: 26px;
                    width: 26px;
                    box-sizing: border-box;
                    background-color: var(--voyage-alarm-red);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color:#353535;
                    border: 2px solid var(--primary-blue-900, blue);
                }

                .vessel-alarm-indicators span[isWeather] {
                    background-color: transparent;
                    border: none
                }
    
                .vessel-alarm-indicators span[isWRAlert] {
                    border: none;
                    background-color: #ffd028;
                }

                .vessel-alarm-indicators span[hasReason="true"]  {
                    border-radius: 30px;
                    padding-left: 1px;
                    height: 26px;
                    width: 26px;
                    box-sizing: border-box;
                    background-color: blue;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color:#353535;
                    border: 2px solid var(--primary-blue-900, blue);
                }

                .vessel-alarm-indicators span[isBasic="true"] {
                    border-radius: 30px;
                    padding-left: 1px;
                    height: 26px;
                    width: 26px;
                    box-sizing: border-box;
                    background-color: var(--voyage-alarm-red);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: none;
                }
                
                .vessel-alarm-indicators span[no-plan] {
                    color: var(--voyage-alarm-red);
                    font-size: 12px;
                    line-height: 16px;
                    background-color: transparent;
                    width: auto;
                    border: none;
                } 

                .vessel-alarm-indicators span[is-handled="true"] {
                    background-color: var(--neutrals-grey-06, grey);
                    border-color: var(--primary-blue-900, blue);
                    border: 2px solid var(--primary-blue-900, blue);
                }

                .vessel-alarm-indicators span[is-handled="true"][isBasic="true"] {
                    background-color: var(--neutrals-grey-06, grey);
                    border: none;
                }

                .vessel-alarm-indicators span[is-handled="false"][isBasic="false"][hasReason="true"] {
                    background-color: var(--warning-yellow-10, yellow);
                    border-color: var(--primary-blue-900, blue);
                    border: 2px solid var(--primary-blue-900, blue);
                }


                .voyage-status p {
                    margin: 0;
                    font-size: 12px;
                    line-height: 16px;
                    color: var(--neutrals-grey-04);
                }

                .voyage-status p:first-child,
                .voyage-status p:only-child {
                    flex: 1 1 100%;
                }
                .voyage-status p:nth-child(2) {
                    text-align: right;
                    flex: 1 0 auto;
                }
                .loading-positions,
                .not-found {
                    color: var(--neutrals-grey-03);;
                    font-size: 16px;
                    padding: 0 16px;
                    font-weight: var(--paragraph-weight, normal);
                    line-height: 24px;
                    text-align: center;
                    white-space: pre-wrap;
                }
                .port {
                    padding: 12px 16px;
                    border-bottom: 1px solid var(--neutrals-grey-06);
                }
                voy-vessel-progress-route {
                    margin-top: 14px;
                }
                .nonmaersk-border {
                    margin-top: 8px;
                }
                .loading-positions-container {
                    display: flex;
                    width: 90%;
                    flex-direction: column;
                    align-items: center;
                    padding: 10px;
                }

                #crosstrack {
                    padding: 0 1px 1px 0;
                }

                #speed {
                    padding: 1px 0 0 1px;
                }

                .premium-icon { 
                    margin-left: -10px;
                }
            `
        ];
    }

    render() {
        return html`
            ${VoyGlobalFilters.isFilterApplied && VoyGlobalSearch.areVesselsLoaded ? html`
                ${this._renderVessels()}
            ` : nothing}
            ${!VoyGlobalSearch.areVesselsLoaded && VoyGlobalFilters.isFilterApplied ? html`
                <div class="loading-positions-container">
                    <mc-loading-indicator fit="large" label="Loading fleet"></mc-loading-indicator>
                </div>
            ` : nothing}
        `;
    }

    private _handleAlarm = async (e: CustomEvent) => {
        if (!e.detail) {
            this._handleAlarmPopupShow = false;
            return;
        }

        await VoyFleetAlarmModel!.updateAlarm(this._selectedAlarm?.id!, e.detail.action, e.detail.selectedReason, e.detail.note);
        // need to rerender element
        this._handleAlarmPopupShow = false;
    }

    private _toggleAlarmPopup(e: CustomEvent): void {
        if(!e.detail.alarmId || !e.detail.alarmType) {
            throw new Error("failed to open handle alarm modal, missing alarmId or alarmType from event details")
        };

        this._handleAlarmPopupShow = !this._handleAlarmPopupShow;
    }

    private _renderVessels(): TemplateResult {
        let className = this.timeLineVisible ? "vessels-container" : "vessels-container full-pannel"
        return html`
             <div class = ${className}  id="parent">
            ${this._lazyLoadVesselList.length > 0
                ? this._lazyLoadVesselList.map(this._renderVessel)
              : html`
               <h2 class="not-found">${VoyFleetSubViewList.emptySearchMessage}</h2>
              `
            }
            </div> 
            <voy-handle-alarm-popup 
                .alarmEventType=${this._selectedAlarm?.alarmType}
                ?shown=${this._handleAlarmPopupShow}
                @close=${this._handleAlarm}>
            </voy-handle-alarm-popup>  
        `
    }

    connectedCallback() {
        super.connectedCallback();

        this._totalVesselsFilteredCount = VoyageShoreContextModel!.vessels.length;
        this._vesselsShownInList = VoyageShoreContextModel!.vessels;
        this._lazyLoadVesselList = this._vesselsShownInList.slice(0,200) 
        this._startVal = 200;
        VoyageShoreContextModel!.addEventListener(ShoreContextEvent.VesselsChange, this._setVessels);
        // Open detailed view on enter when one result is displayed
        this.addEventListener("keydown", this._onEnter);
        VoyageShoreContextModel!.addEventListener(ShoreContextEvent.TimeLineViewToggle, this._toggleTimeLine);
        this.addEventListener("scroll", this.debounce(this._handleScroll,500));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        UiStateModel!.removeEventListener(`${UiStateModelParam.Search}-change`, this._setVessels);
        VoyageShoreContextModel!.removeEventListener(ShoreContextEvent.VesselsChange, this._setVessels);
        VoyageShoreContextModel!.removeEventListener(ShoreContextEvent.TimeLineViewToggle, this._toggleTimeLine);
        this.removeEventListener("keydown", this._onEnter);
        this.removeEventListener("scroll", this.debounce(this._handleScroll,500));
    }

    private _rerenderComponent() {
        this.requestUpdate()
    }

    private _onEnter(event: { key: String }) {
        if (event.key === "Enter" && this._totalVesselsFilteredCount == 1) {
            this._showVesselDetails(this._vesselsShownInList[0].imoNo)
        }
    }

    private _renderAlarmIndicators(vessel: VesselInfo): TemplateResult[] {
        const alarms = VoyFleetAlarmModel!.getVesselAlarmsBasicInfo(vessel.imoNo)
        const hasNoPlanAlarm = alarms.findIndex(a => a.alarmType === AlarmType.NoPlan) > -1

        if (hasNoPlanAlarm) {
            return [html`<span no-plan>No plan</span>`];
        }

        const isBasic = vessel.vesselVersion === VesselVoyageVersion.Basic;
        const indicators: TemplateResult[] = alarms
            .filter(alarm => alarm.alarmState !== AlarmState.Acknowledged)
            .map(a => {
                const isHandled = a.alarmState === AlarmState.InProgress

                let isVesselReasonGiven:boolean = false;

                if( a.allAlarmLogs && a.allAlarmLogs.length > 0) {
                    isVesselReasonGiven = !!a.allAlarmLogs.find(el => el.reasonSource?.toLowerCase() === "vessel" && el.reason != "")
                }
                
                switch (a.alarmType) {
                    case AlarmType.Speed: 
                        return html` 
                        <span id="speed" is-handled=${isHandled} hasReason=${isVesselReasonGiven} isBasic=${isBasic}>
                            ${isHandled || isVesselReasonGiven ? SvgIcons.customSpeedAlarmBlack : SvgIcons.customSpeedAlarm }
                            ${this._renderAlarmTooltip(a, isHandled, isBasic)}
                        </span>
                        `
                    case AlarmType.Stoppage:
                        return html`<span is-handled=${isHandled} hasReason=${isVesselReasonGiven} isBasic=${isBasic}>
                            ${isHandled || isVesselReasonGiven ? SvgIcons.customStoppageAlarmBlack : SvgIcons.customStoppageAlarm }
        
                            ${this._renderAlarmTooltip(a, isHandled, isBasic)}
                        </span>`
                    case AlarmType.Crosstrack:
                        return html`<span id="crosstrack" is-handled=${isHandled} hasReason=${isVesselReasonGiven} isBasic=${isBasic}>
                        ${isHandled || isVesselReasonGiven ? SvgIcons.customOfftrackAlarmBlack : SvgIcons.customOfftrackAlarm }
                        ${this._renderAlarmTooltip(a, isHandled, isBasic)}
                        </span>`
                    case AlarmType.HeavyWeather:
                        return html`<span isWeather>
                            ${SvgIcons.heavyWeatherAlarm}
                            ${this._renderAlarmTooltip(a, isHandled, isBasic)}
                        </span>`
                    case AlarmType.ParametricRoll:
                        return html`<span isWeather>
                            ${SvgIcons.parametricRollAlarm}
                            ${this._renderAlarmTooltip(a, isHandled, isBasic)}
                        </span>`
                     case AlarmType.WRPerformanceAlert:
                        return html`<span isWRAlert>
                            ${SvgIcons.weaterRoutingIcon}
                            ${this._renderAlarmTooltip(a, isHandled, isBasic)}
                        </span>`
                     case AlarmType.WRSafetyAlert:
                        return html`<span isWRAlert>
                            ${SvgIcons.weaterRoutingIcon}
                            ${this._renderAlarmTooltip(a, isHandled, isBasic)}
                        </span>`
                    case AlarmType.WRReject:
                        return html`<span isWRAlert>
                            ${SvgIcons.weaterRoutingIcon}
                            ${this._renderAlarmTooltip(a, isHandled, isBasic)}
                        </span>`
                    default:
                        return html``
                }
            })

        return indicators;
    }

    private _renderAlarmTooltip(alarm: FleetAlarm, isHandled: boolean, isBasic: boolean) {
        if(alarm.alarmType === AlarmType.WRPerformanceAlert || alarm.alarmType === AlarmType.WRSafetyAlert ) {
            return html`<voyage-tooltip-proxy .positionPriority=${["right"]} .tooltipInteraction=${true} .innerComponent=${html`<span>Weather routing advice breaching Maersk safety limits/performance limits.</span>`}></voyage-tooltip-proxy>`
        }
        if(alarm.alarmType === AlarmType.WRReject) {
            return html`<voyage-tooltip-proxy .positionPriority=${["right"]} .tooltipInteraction=${true} .innerComponent=${html`<span>Weather routing advice rejected by vessel</span>`}></voyage-tooltip-proxy>`
        }
        //content for toolrip based on type
        let innerComponent
        let buttonsComponent
        const alarmType =  alarm.alarmType == AlarmType.Crosstrack ? "Offtrack" :  alarm.alarmType;
        const alarmStartDate = GetFormattedDateDayMonthTimeNoYear(alarm.startTs)
        
            if(isHandled) {
                if(alarm.alarmState === AlarmState.Acknowledged) {
                    buttonsComponent = html``
                } else {
                    buttonsComponent = html`
                    <style>
                        .buttons-wrapper {
                            margin-top: 8px;
                            display: flex;
                            flex-direction: row;
                            align-items: flex-end;
                            justify-content: flex-end;
                        }
                    </style>
                    ${isBasic 
                        ? html`
                            <div class="buttons-wrapper">
                                <mc-button appearance="neutral" label="Dismiss" fit="small" label="Dismiss" @click=${() => this._onAlarmDismiss(alarm, isBasic)}></mc-button>
                             </div>`
                            : html`
                            ${this._getAlarmReasonStatusFromVessel(alarm) 
                            ? html` <div class="buttons-wrapper"><mc-button appearance="neutral" label="Dismiss" fit="small" label="Dismiss" @click=${() => this._onAlarmDismiss(alarm, isBasic)}></mc-button></div>`
                            :  nothing }
                        `}                       
                    `
                }
            } else {
                buttonsComponent = html`
                 <style>
                    .buttons-wrapper {
                        margin-top: 8px;
                        display: flex;
                        flex-direction: row;
                        justify-content: flex-start;
                        gap: 16px;
                    }
                </style>
                ${isBasic 
                     ? html`
                        <div class="buttons-wrapper">
                            <mc-button appearance="neutral" fit="small" label="In progress" @click=${() => this._onAlarmInProgress(alarm.id)} type="button"></mc-button>
                            <mc-button appearance="neutral" fit="small" label="Dismiss" @click=${() => this._onAlarmDismiss(alarm, isBasic)} type="button"></mc-button>
                        </div>
                    `: html`
                        ${this._getAlarmReasonStatusFromVessel(alarm) 
                            ? html`
                                <div class="buttons-wrapper">
                                    <mc-button appearance="neutral" fit="small" label="In progress" @click=${() => this._onAlarmInProgress(alarm.id)} type="button"></mc-button>
                                    <mc-button appearance="neutral" fit="small" label="Dismiss" @click=${() => this._onAlarmDismiss(alarm, isBasic)} type="button"></mc-button>
                                </div>
                            `
                            : nothing }
                        ` }
                `              
        }
        innerComponent = html`
        <style>
            .innerComponent .content .alarm-title {
                font-family: MaerskText;
                font-size: 12px;
                font-weight: 700;
                line-height: 16px;
                letter-spacing: 0em;
                text-align: left;
                padding: 0px;
                margin:0px;
                text-transform: capitalize;
            }
            .innerComponent .content .alarm-body {
                font-family: MaerskText;
                font-size: 12px;
                font-weight: 400;
                line-height: 16px;
                letter-spacing: 0em;
                text-align: left;
                padding: 0px;
                margin-top: 0px;
                margin-bottom: 0px;
            }
            .innerComponent .content .reason {
                 margin-top: 4px;
                 margin-bottom: 4px;
            }
            .innerComponent .content .comment {
                 margin-bottom: 4px;
            }
            .innerComponent .content {
                margin: 0px;
            }
            .innerComponent .content {
                display: flex;
                justify-content: space-evenly;
                flex-direction: column;
            }
            .alarm-date {
                font-weight: 400;
            }
        </style>
        <div class="content">
            <p class="alarm-title">${alarmType}<span class = "alarm-date"> | ${alarmStartDate} UTC</span> </p>
            <p class="alarm-body">${getStaticInfoWithTimeRemaining(alarm)}</p>
            ${this._showReason(isBasic, alarm)? html`${this._renderReasonFromVessel(alarm)}` : nothing }
            ${(showHandleAlarm(alarm)) ? html`${buttonsComponent}` : nothing }
        </div>`

        return html`<voyage-tooltip-proxy .positionPriority=${["right"]} .tooltipInteraction=${true} .innerComponent=${innerComponent}></voyage-tooltip-proxy>`
    }

    private _showReason(isBasic: boolean, alarm: FleetAlarm) {
        if(!isBasic && !PremiumVesselNotInNextPlatform?.find(imo => imo === alarm?.imo) ) {
            if(alarm.alarmType !== AlarmType.HeavyWeather && alarm.alarmType !== AlarmType.ParametricRoll) {
                if(this._getAlarmReasonStatusFromVessel(alarm)) return true
            }
        }
        return false
    }

    private _getAlarmReasonStatusFromVessel(alarm: FleetAlarm){
        let vesselCommentStatus = false
        let vesselReason = []
        if(alarm.allAlarmLogs && alarm.allAlarmLogs[0].reasonSource?.toLowerCase() === "vessel") vesselCommentStatus = true
        // once user change the alarm status(inprogress to acknowledged - vessel submitted reason should be available in log)
        else if(alarm.allAlarmLogs && alarm.allAlarmLogs.length){
            vesselReason = alarm.allAlarmLogs.filter(log => log.reasonSource?.toLowerCase() === 'vessel')

            if(vesselReason.length) vesselCommentStatus = true
        }
        // CLF feature enabled for premium vessels now -excluded the vessels without next platform
        return vesselCommentStatus || PremiumVesselNotInNextPlatform?.find(imo => imo === alarm.imo);
    }

    private async _onAlarmDismiss(alarm: FleetAlarm, isBasic: Boolean) {
        // CLF feature enabled for premium vessels now -excluded the vessels without next platform
        if(isBasic || PremiumVesselNotInNextPlatform?.find(imo => imo === alarm.imo)) {
            this._handleAlarmPopupShow = true;
            this._selectedAlarm = alarm;
        } else {
            await VoyFleetAlarmModel!.updateAlarm(alarm?.id,AlarmState.Acknowledged, "", "");
            this._rerenderComponent();
        }       
    }

    private _renderReasonFromVessel(alarm: FleetAlarm) {
        let reasonFromVessels = alarm.allAlarmLogs?.filter(log => log.reasonSource?.toLowerCase() === "vessel")
        if(reasonFromVessels?.length && !PremiumVesselNotInNextPlatform?.find(imo => imo === alarm.imo) ){
            return html`
            <p class="alarm-body reason">Reason by vessel: ${GetUTCTimestamp(alarm?.startTs||"")} UTC ${reasonFromVessels[0].reason}</p>
            ${reasonFromVessels[0].note ? html`<p class="alarm-body comment">Comment: ${reasonFromVessels[0].note}</p>`: nothing }
            `
        } 
        else if(alarm.alarmLogs && alarm.alarmLogs.length){
            let replyFromVessel =  alarm.alarmLogs?.filter(log => log.reasonSource?.toLowerCase() === "vessel")
            if(replyFromVessel?.length) {
                return html`
                <p class="alarm-body reason">Reason by vessel: ${GetUTCTimestamp(replyFromVessel[0]?.createdAt||"")} UTC ${replyFromVessel[0]?.reason}</p>
                ${replyFromVessel[0]?.note ? html`<p class="alarm-body comment">Comment: ${replyFromVessel[0]?.note}</p>`: nothing }
                `
            }
        }
    }

    private async _onAlarmInProgress(id: number){
        await VoyFleetAlarmModel!.updateAlarm(id, AlarmState.InProgress ,"");
        this._rerenderComponent();
    }

    private _renderVessel(vessel: VesselInfo): TemplateResult {
        
        const schedule = VoyageSchedulesModel?.currentScheduleStates?.find(s => s.imoNo === vessel.imoNo)
        const isNonMaerskVessel = vessel.operatorCode === OperatorId.NonMSK;

        const terminalInfo = schedule && TerminalsModel!.getTerminal(schedule.schedule.endPort);
        const formattedETA = schedule && terminalInfo && FormatDateToLocalTime(schedule.schedule.endTime, terminalInfo.olsonTimezone!);

        const isPremium = vessel.vesselVersion === VesselVoyageVersion.Premium;

        return html`
            <div id=${`vessel-${vessel.imoNo}`} class="vessel" @click=${() => this._showVesselDetails(vessel.imoNo)}>
                <div class="vessel-header">
                    <span class="premium-icon" ?hidden=${!isPremium}>${SvgIcons.customPremiumVessel}</span>
                    <h4>${vessel.vesselName}</h4>
                    <div class="vessel-alarm-indicators" @click=${(e: Event) => this._onAlarmsClick(e, vessel.imoNo)}>
                        ${this._renderAlarmIndicators(vessel)}
                    </div>
                </div>
                ${schedule && schedule.schedule.inPort === false
                ? html`
                        <div class="voyage-status">
                            <p data-test="start-end-ports">${schedule?.schedule.startPort} ➔ ${schedule?.schedule.endPort}</p>
                            <p data-test="eta">${formattedETA}</p>
                        </div>
                    `
                : html``
            }
                ${schedule && schedule.schedule.inPort === true
                ? html`
                        <div class="voyage-status">
                            <p data-test="vessel-in-port">Vessel is in port: (${schedule?.schedule.endPort})</p>
                        </div>
                    `
                : html``
            }
                ${!schedule
                ? html`
                        <div class="voyage-status">
                            <p data-test="unavailable">Schedule unavailable</p>
                        </div>
                    `
                : html``
            }
                ${isNonMaerskVessel ? html`<div class="nonmaersk-border"></div>` : html`<voy-vessel-progress-route .imo=${vessel.imoNo}></voy-vessel-progress-route>`}
            </div>
        `
    }

    private _onAlarmsClick(e: Event, imo: number) {
        e.stopPropagation();
        UiStateModel!.setValue(UiStateModelParam.IMO, imo.toString());
        UiStateModel!.setValue(UiStateModelParam.FISubView, FiSubView.VesselAlarms)
    }

    private _setVessels(): void {
        this._totalVesselsFilteredCount = VoyageShoreContextModel!.vessels.length;
        this._vesselsShownInList = VoyageShoreContextModel!.vessels;
        this._lazyLoadVesselList = this._vesselsShownInList.slice(0,200) 
        this._startVal = 200;

    }

    public _showVesselDetails(imo: number): void {
        UiStateModel!.setValue(UiStateModelParam.IMO, imo.toString());
        UiStateModel!.setValue(UiStateModelParam.PORT);
        UiStateModel!.setValue(UiStateModelParam.PortRegion);
        UiStateModel!.setValue(UiStateModelParam.FISubView, FiSubView.VesselDetails);
        UiStateModel!.setLeftPanelVisibility(true,"hide-anchor");
        TraceService!.event(
            FI_EVENT_CATEGORIES.VESSEL_LIST,
            FI_VESSEL_LIST_ACTIONS.SELECTED_VESSEL,
            imo.toString()
        );
    }

    private _toggleTimeLine():void {
        this.timeLineVisible = !this.timeLineVisible
    }

    private debounce(func: Function, delay:number) {
        let timeoutId:any;
        return  () => {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            func.apply(this, arguments);
          }, delay);
        };
      }

      private _handleScroll():void {

        const remainingItems = this._vesselsShownInList.length - this._lazyLoadVesselList.length;
        if (remainingItems > 0) {
            // Calculate the number of items to copy in the current iteration (up to 200)
            const itemsToCopy = Math.min(remainingItems, 200);
            // Copy the items to _lazyLoadVesselList
            this._lazyLoadVesselList = [
                ...this._lazyLoadVesselList,
                ...this._vesselsShownInList.slice(this._startVal, (this._startVal + itemsToCopy))
            ];

            // Update start value for the next iteration
            this._startVal = this._startVal + itemsToCopy;
        } 
    }

}
